const _excluded = ["value", "className", "placeholder", "debounceTime", "registerHotkey", "showHint", "onChange", "onClear", "onInputBlur", "onInputFocus"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import React, { useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import debounce from 'lodash/debounce';
import { Search } from "../../icons";
import { CloseButton } from "../CloseButton";
import styles from "./Searchbox.module.scss";
import { useOverlayContext } from "../OverlayContext";
export function Searchbox(props) {
  const {
      value,
      className,
      placeholder,
      debounceTime,
      registerHotkey = false,
      showHint = false,
      onChange,
      onClear,
      onInputBlur,
      onInputFocus
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  const [inputValue, setInputValue] = useState(value || '');
  const {
    hasOpenModals
  } = useOverlayContext();
  const inputValueBeforeDebounce = useRef(inputValue);
  const debouncedOnChange = useMemo(() => {
    return debounce(event => {
      if (inputValueBeforeDebounce.current !== event.target.value) {
        onChangeRef.current(event);
      }
    }, debounceTime);
  }, [debounceTime]);
  const wrappedOnChange = event => {
    inputValueBeforeDebounce.current = event.target.value;
    onChange && onChange(event);
  };
  const onChangeRef = useRef(wrappedOnChange);
  onChangeRef.current = wrappedOnChange;
  const debouncedOnChangeRef = useRef(debouncedOnChange);
  debouncedOnChangeRef.current = debouncedOnChange;
  const searchBoxRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isHotkeyRegistered, setIsHotkeyRegistered] = useState(false);
  useEffect(() => {
    function onKeyDown(ev) {
      if (ev.key === '/') {
        ev.preventDefault();
        searchBoxRef.current != null && searchBoxRef.current.focus();
      }
    }
    if (registerHotkey && !isFocused && !hasOpenModals) {
      document.addEventListener('keydown', onKeyDown, true);
      setIsHotkeyRegistered(true);
      return () => {
        document.removeEventListener('keydown', onKeyDown, true);
        setIsHotkeyRegistered(false);
      };
    }
  }, [registerHotkey, isFocused, hasOpenModals]);
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);
  useEffect(() => {
    return () => {
      debouncedOnChangeRef.current.cancel();
    };
  }, []);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cn(styles.root, className)
  }, rest), /*#__PURE__*/React.createElement(Search, {
    className: styles.searchIcon
  }), /*#__PURE__*/React.createElement("input", {
    ref: searchBoxRef,
    type: "text",
    className: cn(styles.input, inputValue.length > 0 ? styles.notEmpty : null),
    value: inputValue,
    placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : 'Search...',
    "data-testid": props['data-testid'] && "".concat(props['data-testid'], "-input"),
    "data-hotkey-registered": isHotkeyRegistered,
    onChange: event => {
      setInputValue(event.target.value);
      if (debounceTime) {
        event.persist();
        debouncedOnChange(event);
      } else {
        onChangeRef.current(event);
      }
    },
    onFocus: ev => {
      setIsFocused(true);
      onInputFocus === null || onInputFocus === void 0 || onInputFocus(ev);
    },
    onBlur: ev => {
      setIsFocused(false);
      onInputBlur === null || onInputBlur === void 0 || onInputBlur(ev);
    }
  }), registerHotkey && showHint && /*#__PURE__*/React.createElement("div", {
    className: styles.hotkeyHint
  }, /*#__PURE__*/React.createElement("kbd", null, "/")), !!inputValue && /*#__PURE__*/React.createElement(CloseButton, {
    className: styles.clear,
    size: "small",
    "data-testid": props['data-testid'] && "".concat(props['data-testid'], "-clear"),
    onClick: () => {
      // In uncontrolled mode, clear the value automatically.
      if (typeof value !== 'string') {
        setInputValue('');
      }
      onClear && onClear();
    }
  }));
}